import { useContext, useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "@contexts/User";
import { USER_ROLES } from "@utils/consts";
import { Inbox, Trash, Archive, Note } from "@assets/icons";
import "react-toastify/dist/ReactToastify.css";

const BaseToast = ({ closeToast, icon, message }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-row min-h-[56px] justify-between items-center bg-dark-gray px-4 py-4 rounded-lg mb-6">
      <div className="flex min-w-0 items-center gap-3 flex-1 ">
        <div className="mt-1 shrink-0">{icon}</div>
        <div className="text-white text-sm overflow-hidden text-ellipsis min-w-0">
          {typeof message === "string" ? (
            <span className="font-medium-poppins">{message}</span>
          ) : (
            <span dangerouslySetInnerHTML={{ __html: message }} />
          )}
        </div>
      </div>

      <button
        onClick={closeToast}
        className="text-primary text-sm ml-4 shrink-0 font-medium-poppins"
        style={{ minWidth: "4rem" }}
      >
        {t("common.close")}
      </button>
    </div>
  );
};

const Info = ({ closeToast, message }) => (
  <BaseToast closeToast={closeToast} message={message} icon={<Inbox />} />
);
const Deleted = ({ closeToast, message }) => (
  <BaseToast
    closeToast={closeToast}
    message={message}
    icon={<Trash className="fill-current text-white" />}
  />
);
const ChatArchived = ({ closeToast, message }) => (
  <BaseToast
    closeToast={closeToast}
    message={message}
    icon={<Archive className="fill-current text-white" />}
  />
);
const Success = ({ closeToast, message }) => (
  <BaseToast
    closeToast={closeToast}
    message={message}
    icon={<Note className="fill-current text-white" />}
  />
);
const PopupError = ({ closeToast, message }) => (
  <BaseToast
    closeToast={closeToast}
    message={message}
    icon={<Note className="fill-current text-red" />}
  />
);

const NotifyByType = {
  info: Info,
  deleted: Deleted,
  chatArchived: ChatArchived,
  success: Success,
  error: PopupError,
};

export const sendNotify = (message, type) => {
  const Component = NotifyByType[type];
  toast(<Component message={message} />);
};

// Student role below 1280px: the toast fits the screen (16px side gutter, max 450px) instead of a fixed
// 450px that overflows phones, and on phones it sits above the bottom tab bar. These classes only apply
// inside the Student container (see below), so other roles keep the default toast.
const toastClass =
  "p-0 bg-transparent shadow-none mb-[50px] w-[450px] min-h-[56px] max-xl:group-[.student-toasts]:w-[calc(100vw-2rem)] max-xl:group-[.student-toasts]:max-w-[450px] max-md:group-[.student-toasts]:mb-[5.25rem]";
const bodyClass = "p-0 m-0 w-full h-full flex items-stretch";

const GlobalNotify = () => {
  const { profile } = useContext(UserContext);
  // Remembers the last logged-in role, so toasts shown right after a Student logs out
  // ("logged out successfully") keep the Student sizes; it changes only when another profile loads
  const [isStudent, setIsStudent] = useState(false);
  useEffect(() => {
    if (profile?.type) setIsStudent(profile.type === USER_ROLES.STUDENT_ROLE);
  }, [profile?.type]);

  return (
  <ToastContainer
    // Marks the container for Students; it updates live, so toasts sent before the profile has loaded
    // (e.g. "logged in successfully") still get the Student sizes once it is known
    className={isStudent ? "group student-toasts" : undefined}
    position="bottom-center"
    autoClose={2000}
    closeButton={false}
    hideProgressBar
    newestOnTop
    rtl={false}
    pauseOnFocusLoss
    draggable
    pauseOnHover
    toastClassName={() => toastClass}
    bodyClassName={() => bodyClass}
  />
  );
};

export default GlobalNotify;
